import React, { useEffect, useState } from 'react';
import Loader from 'react-loader';

import { Report, Query, SearchResults, SortFields } from 'tcf-upstream-shared/models';
import { REPORTS_URL } from 'tcf-upstream-shared/urls';

import { useAppDispatch, useAppSelector } from '../../../hooks';
import { setQueryPagingAndSorting } from '../../../actions/queryActions';
import { readReports } from '../../../actions/reportActions';
import * as formatting from '../../../utils/formatting';
import TcfBootstrapTable, {
  getDefaultSorted,
  getPagingParamsForQuery,
  getPagingParamsForTable,
} from '../../../components/TcfBootstrapTable';
import ErrorComponent from '../../AsyncPage/ErrorComponent';
import ReportLink from '../../../components/ReportLink';

const DEFAULT_PAGE_SIZE = 25;
const DEFAULT_SORT: SortFields = [{ createdOn: 'desc' }];

interface ReportListProps {
  storeIdentifier: string;
  queryIdentifier: string;
}

const ReportList = (props: ReportListProps) => {
  const dispatch = useAppDispatch();
  const [delayRender, setDelayRender] = useState(true);
  const { storeIdentifier, queryIdentifier } = props;

  const payload: SearchResults = useAppSelector((state) => state.serverStores?.[storeIdentifier]?.payload);
  const error: string | undefined = useAppSelector((state) => state.serverStores?.[storeIdentifier]?.error);
  const isFetching: boolean = useAppSelector((state) => state.serverStores?.[storeIdentifier]?.isFetching);
  const query: Query = useAppSelector((state) => state.queries[queryIdentifier]);
  const queryString = JSON.stringify(query);

  useEffect(() => {
    const _query = queryString ? JSON.parse(queryString) : undefined;
    if (!_query) {
      dispatch(setQueryPagingAndSorting(queryIdentifier, { skip: 0, limit: DEFAULT_PAGE_SIZE, sort: DEFAULT_SORT }));
    }
  }, [queryString, queryIdentifier, dispatch]);

  useEffect(() => {
    const _query = queryString ? JSON.parse(queryString) : {};
    _query.sort = _query.sort || DEFAULT_SORT;
    dispatch(readReports(storeIdentifier, _query));
  }, [queryString, storeIdentifier, dispatch]);

  useEffect(() => {
    setDelayRender(false);
  }, []);

  if (isFetching || delayRender) {
    return <Loader loaded={false} />;
  } else if (error) {
    return <ErrorComponent error={error} />;
  } else if (!payload?.total || payload.total < 1) {
    return null;
  }

  const { currentPage, limit, totalSize, sort } = getPagingParamsForTable(payload, query, DEFAULT_PAGE_SIZE, DEFAULT_SORT);

  const onTableChange = (type: unknown, newState: any) => {
    const q = getPagingParamsForQuery(newState);
    dispatch(setQueryPagingAndSorting(queryIdentifier, q));
  };

  const dataSource = (payload?.results || []).map((o) => ({
    ...o,
  }));

  const formatTitle = (cell: string, row: Report) => {
    const f = {
      id: row.id,
      original_file_name: row.originalFileName,
      new_file_name: row.newFileName,
    };
    return (
      <ReportLink
        file={f}
        reportsUrl={REPORTS_URL}
        className="tableButton btn btn-link btn-sm"
        style={{ border: 0, marginLeft: 0 }}
        title={'Download report file'}
      >
        {row.title}
      </ReportLink>
    );
  };

  const columns = [
    {
      dataField: 'createdOn',
      text: 'Report Date',
      sort: true,
      formatter: formatting.formatLocalDate,
    },
    {
      dataField: 'title',
      text: 'Title',
      formatter: formatTitle,
      sort: true,
    },
    {
      dataField: 'extension',
      text: 'File Type',
      sort: false,
    },
    {
      dataField: 'size',
      text: 'File Size',
      sort: false,
      formatter: formatting.humanizeBytes,
    },
  ];

  return (
    <TcfBootstrapTable
      allowPaging
      allowWrap
      columns={columns}
      dataSource={dataSource}
      defaultSorted={getDefaultSorted(sort)}
      onTableChange={onTableChange}
      page={currentPage}
      remote
      sizePerPage={limit}
      totalSize={totalSize}
    />
  );
};

export default ReportList;
